import { useState, useEffect } from "react";
import PropTypes from "prop-types";

import Button from "@shared/v2/Button";
import { PlusSolidV6 } from "@shared/v2/Icomoon";
import AgentListingFilters from "./AgentListingFilters";
import AgentStats from "./AgentStats";
import AgentListingsTable from "./AgentListingsTable";
import ListingsMap from "./ListingsMap";
import { getStatusValues } from "../styleHelpers";
import { useStateWithProps } from "../customHooks";
import DateDropdown from "./DateDropdown";
import { useFeatureFlags } from "../../../reducers/layoutReducer/selectors";

const DEFAULT_STATUSES = ["Active", "Pending", "Contingent", "Sold", "Off Market"];

const DATE_FILTER_OPTIONS = [
  { label: "6 Months", value: "6" },
  { label: "1 Year", value: "12" },
  { label: "2 Years", value: "24" },
];

const Disclaimer = ({ showListingLimitDisclaimer, marginBottom, monthsBack, onDateChosen }) => {
  const monthsBackOption = DATE_FILTER_OPTIONS.find((o) => o.value === monthsBack);
  const textColor = showListingLimitDisclaimer ? "tw-text-brivity-coral-100" : "tw-text-gray-50";
  const verbiage = showListingLimitDisclaimer
    ? `(Maxed out) Displaying agent's 1,000 most recent listings from the last ${monthsBackOption.label.toLowerCase()}.`
    : "";

  return (
    <div className={`tw-flex tw-flex-row tw-items-center ${marginBottom}`}>
      <span className="tw-text-14px tw-text-gray-75 tw-mr-8px">Displaying data from the last:</span>
      <DateDropdown monthsBack={monthsBackOption} options={DATE_FILTER_OPTIONS} onDateChosen={onDateChosen} />
      <span className={`tw-ml-8px ${textColor} tw-text-10px tw-italic`}>{verbiage}</span>
    </div>
  );
};

Disclaimer.propTypes = {
  showListingLimitDisclaimer: PropTypes.bool.isRequired,
  marginBottom: PropTypes.string.isRequired,
  monthsBack: PropTypes.string.isRequired,
  onDateChosen: PropTypes.func.isRequired,
};

const openAddToCrmModal = (agent, office) => {
  const { name } = agent;
  let firstName = "";
  let lastName = "";
  if (name) {
    // Naive guessing at how to split up possible agent name;
    const space = name.indexOf(" ");

    firstName = name.substring(0, space).trim();
    lastName = name.substring(space + 1).trim();
  }

  const brokerageDescription = office.name ? `${office.name}\n` : "";
  const description = `${brokerageDescription}MLS Agent ID#${agent.id}`;

  Brivity.CreateLead.createRecruitModal(firstName, lastName, agent.email, agent.phone, description);
};

const AgentDetails = ({ agent, office, listingData, monthsBack, onDateChosen, onClose }) => {
  const [selectedStatuses, setSelectedStatuses] = useState(DEFAULT_STATUSES);
  const [listingsToShow, setListingsToShow] = useStateWithProps(listingData.listings);
  const featureFlags = useFeatureFlags();

  const showListingLimitDisclaimer = listingData.listings?.length >= 1000;

  const onToggleStatus = (status) => {
    const filtered = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status];

    setSelectedStatuses(filtered);
  };

  useEffect(() => {
    const filtered =
      listingData.listings?.filter(({ standardStatus }) => {
        const mapped = getStatusValues(standardStatus).text;

        return selectedStatuses.includes(mapped);
      }) || [];

    setListingsToShow(filtered);
  }, [selectedStatuses, listingData]);

  return (
    <div className="tw-w-full tw-flex tw-box-border tw-flex-col tw-text-gray-50">
      <p className="tw-mb-4px">
        {office.name || "Unknown office"}
        <span className="tw-text-color-ebebeb tw-mx-12px">|</span>Office ID: #{office.id}
        <span className="tw-text-color-ebebeb tw-mx-12px">|</span>Agent ID: #{agent.id}
      </p>
      <p className="tw-mb-12px">
        {agent.phone || "Unknown phone number"}
        <span className="tw-text-color-ebebeb tw-mx-12px">|</span>
        {agent.email || "Unknown email address"}
      </p>
      <div className="tw-mb-24px">
        <Button
          className="tw-flex tw-items-center tw-gap-[6px]"
          onClick={() => onClose() || openAddToCrmModal(agent, office)}
        >
          <PlusSolidV6 />
          Add To CRM
        </Button>
      </div>
      <Disclaimer
        showListingLimitDisclaimer={showListingLimitDisclaimer}
        marginBottom="tw-mb-4px"
        monthsBack={monthsBack}
        onDateChosen={onDateChosen}
      />
      {featureFlags?.partnerSearch && (
        <span className="tw-text-gray-50 tw-text-12d tw-italic tw-mb-16px">
          These estimated metrics are based on a variety of sources and may not represent finalized agent
          production
        </span>
      )}
      <div className="tw-mb-24px">
        <AgentStats listingStats={listingData?.stats} />
      </div>
      <div className="tw-mb-12px">
        <AgentListingFilters
          selectedStatuses={selectedStatuses}
          availableStatuses={listingData.availableStatuses}
          onToggleStatus={onToggleStatus}
        />
      </div>
      <hr className="tw-w-full tw-bg-gray-10 tw-mb-24px tw-mt-0" />
      <div className="tw-mb-40px">
        <ListingsMap listings={listingsToShow} />
      </div>
      <Disclaimer
        showListingLimitDisclaimer={showListingLimitDisclaimer}
        marginBottom="tw-mb-12px"
        monthsBack={monthsBack}
        onDateChosen={onDateChosen}
      />
      <AgentListingsTable mlsListings={listingsToShow} />
    </div>
  );
};

AgentDetails.propTypes = {
  agent: PropTypes.shape().isRequired,
  office: PropTypes.shape().isRequired,
  listingData: PropTypes.shape().isRequired,
  monthsBack: PropTypes.string.isRequired,
  onDateChosen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AgentDetails;
